<!--
  @description Main site navigation bar used on all public website pages. Renders a desktop
  horizontal nav (links + sign-in or user avatar) and a mobile hamburger menu backed by a
  slide-in SideMenu. Switches layout at 960 px. When the user is authenticated the avatar
  opens a right-hand settings drawer with a start-game shortcut and logout button. Applies a
  CSS `slide-up` animation on the header element when navigating to the game.

  @example
  <Navbar />

  @accessibility Uses a semantic header and nav with aria-label="Main Navigation". Mobile
  hamburger toggle carries aria-label="Toggle Mobile Menu". All sign-in and user-menu
  buttons carry explicit aria-label attributes.
-->
<template>
  <header ref="headerRef" class="navbar">
    <nav aria-label="Main Navigation" class="navbar__nav">
      <div class="navbar__desktop-nav">
        <BrandLogo />
        <ul class="navbar__links" v-show="!isMobile">
          <NavItem :to="{ name: 'home' }" :label="$t('nav.home')" />
          <NavItem :to="{ name: 'what' }" :label="$t('nav.what')" />
          <NavItem :to="{ name: 'rules' }" :label="$t('nav.rules')" />
          <NavItem :to="{ name: 'about' }" :label="$t('nav.about')" />
          <NavItem :to="{ name: 'contact' }" :label="$t('nav.contact')" />
        </ul>
        <div class="navbar__actions" v-show="!isMobile">
          <router-link
            :to="{ name: 'auth' }"
            class="sign-in no-active mr-1"
            aria-label="Sign In"
            v-if="!authStore.isAuthenticated"
            ><button type="button" class="secondary">
              {{ $t('nav.connection') }}
            </button></router-link
          >
          <button
            class="no-style-button"
            @click="toggleRightMenu"
            v-if="authStore.isAuthenticated"
            aria-label="User Menu"
          >
            <img
              class="navbar__user-avatar"
              :src="
                user?.photoURL
                  ? user?.photoURL
                  : currentUser?.photoURL
                    ? currentUser.photoURL
                    : 'https://cdn-icons-png.flaticon.com/512/149/149071.png'
              "
              alt="User image"
              ref="userRef"
            />
          </button>
        </div>
      </div>
      <div ref="icon" class="navbar__hamburger-wrapper" v-show="isMobile">
        <div
          class="navbar__hamburger"
          @click="openMobileNav()"
          :class="{ 'navbar__hamburger--active': mobileMenu }"
          aria-label="Toggle Mobile Menu"
        >
          <span class="navbar__hamburger-line navbar__hamburger-line--1"></span>
          <span class="navbar__hamburger-line navbar__hamburger-line--2"></span>
          <span class="navbar__hamburger-line navbar__hamburger-line--3"></span>
        </div>
      </div>
      <SideMenu
        :isOpen="mobileMenu"
        :isClosable="false"
        :leftMenu="true"
        @close="toggleMobileNav"
        class="navbar__slider"
        aria-label="Mobile Menu"
      >
        <div class="navbar__menu-content">
          <ul>
            <NavItem :to="{ name: 'home' }" :label="$t('nav.home')" @click="toggleMobileNav" />
            <NavItem :to="{ name: 'what' }" :label="$t('nav.what')" @click="toggleMobileNav" />
            <NavItem :to="{ name: 'rules' }" :label="$t('nav.rules')" @click="toggleMobileNav" />
            <NavItem :to="{ name: 'about' }" :label="$t('nav.about')" @click="toggleMobileNav" />
            <NavItem
              :to="{ name: 'contact' }"
              :label="$t('nav.contact')"
              @click="toggleMobileNav"
            />
          </ul>
          <ul class="navbar__mobile-links" v-if="authStore.isAuthenticated">
            <NavItem
              :to="{ name: 'parameters' }"
              :label="$t('nav.menu.settings')"
              @click="toggleMobileNav"
            />
          </ul>
        </div>
        <div class="navbar__menu-footer">
          <router-link
            :to="{ name: 'auth' }"
            class="no-active w-full"
            @click="toggleMobileNav"
            aria-label="Sign In"
            ><button class="secondary ml-0 full-width" v-if="!authStore.isAuthenticated">
              {{ $t('nav.connection') }}
            </button></router-link
          >
          <button
            type="button"
            v-show="authStore.isAuthenticated"
            class="secondary full-width"
            @click="startGame"
          >
            {{ $t('nav.menu.start-game') }}
          </button>
          <button
            type="button"
            v-show="authStore.isAuthenticated"
            @click="handleSignOut"
            class="primary full-width"
          >
            {{ $t('nav.logout') }}
          </button>
        </div>
      </SideMenu>
    </nav>
    <SideMenu
      :isOpen="settingsOpen"
      @close="toggleRightMenu"
      class="navbar__slider"
      aria-label="Settings Menu"
    >
      <div class="navbar__menu-content">
        <ul>
          <li>
            <router-link :to="{ name: 'parameters' }" aria-label="Settings" @click="close">{{
              $t('nav.menu.settings')
            }}</router-link>
          </li>
        </ul>
      </div>
      <div class="navbar__menu-footer">
        <button
          type="button"
          v-show="authStore.firebaseUser"
          class="secondary full-width"
          @click="startGame"
          aria-label="Start Game"
        >
          {{ $t('nav.menu.start-game') }}
        </button>
        <button
          type="button"
          v-show="authStore.firebaseUser"
          @click="handleSignOut"
          class="primary full-width"
          aria-label="Logout"
        >
          {{ $t('nav.logout') }}
        </button>
      </div>
    </SideMenu>
  </header>
</template>

<script setup lang="ts">
import SideMenu from '@/components/common/Global/side-menu/SideMenu.vue'
import BrandLogo from '@/components/common/navbar/BrandLogo.vue'
import NavItem from '@/components/common/navbar/NavItem.vue'

import type { User } from '@/types/Types'

import { onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'

import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/authStore'
import authService from '@/stores/authService'

const authStore = useAuthStore()
const router = useRouter()
const toast = useToast()
const { t, locale } = useI18n()

const isMobile = ref(false)
const mobileMenu = ref(false)
const settingsOpen = ref(false)
const currentUser = ref()
const headerRef = ref<HTMLElement | undefined>()
const user = ref<User | null>()
const doubleClick = ref(false)

function toggleMobileNav() {
  mobileMenu.value = !mobileMenu.value
  setTimeout(() => {
    doubleClick.value = false
  }, 500)
}

function openMobileNav() {
  if (!doubleClick.value) {
    doubleClick.value = true
    mobileMenu.value = true
    return
  }
  doubleClick.value = false
}

function checkScreenSize() {
  if (window.innerWidth < 960) {
    isMobile.value = true
    settingsOpen.value = false
    return
  }
  isMobile.value = false
  return
}

function toggleRightMenu() {
  settingsOpen.value = !settingsOpen.value
}

function handleSignOut() {
  authService
    .signOut()
    .then(() => {
      toast.success(t('nav.messages.logout-success'))
      router.push({ name: 'home' })
      settingsOpen.value = false
      mobileMenu.value = false
      authStore.firebaseUser = null
    })
    .catch(() => {
      toast.error(t('nav.messages.logout-error'))
    })
}

async function validateUserIsSignedIn() {
  if (authStore.firebaseUser) {
    currentUser.value = authStore.firebaseUser
    user.value = authStore.dbUser
  }
}

function startGame() {
  if (headerRef.value) {
    headerRef.value.classList.add('navbar--slide-up')
    settingsOpen.value = false
    mobileMenu.value = false
    setTimeout(() => {
      router.push({ name: 'main-menu' })
    }, 300)
  }
}

function close() {
  settingsOpen.value = false
}

function getLocale() {
  if (user.value?.preferredLanguage) {
    locale.value = user.value.preferredLanguage
    localStorage.setItem('locale', locale.value)
  } else {
    const localLocale = localStorage.getItem('locale')
    if (localLocale) {
      locale.value = localLocale
    }
  }
}

onMounted(async () => {
  checkScreenSize()
  await validateUserIsSignedIn()
  getLocale()
  window.addEventListener('resize', checkScreenSize)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkScreenSize)
})
</script>

<style scoped lang="scss">
@import './Navbar.scss';
</style>
